import { ComponentType, lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Center, Loader } from "@mantine/core";
import AppShell from "./components/AppShell";
import { useAuth } from "./auth";

// Wraps React.lazy with a single retry on chunk-load failure. If the OLD
// index.html is cached but the chunk filename rotated, this catches the
// failed import, force-reloads to fetch fresh index.html + new chunks, and
// returns a no-op component while navigation tears down. The main.tsx
// global listeners handle the same failure for `<script>`-level preloads;
// this catches it at the React Suspense level.
function lazyWithReload<T extends ComponentType<unknown>>(
  loader: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await loader();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("Failed to fetch dynamically imported module") || msg.includes("error loading dynamically imported module")) {
        const RELOAD_FLAG = "wptw:chunk-reload-fired";
        try {
          if (window.sessionStorage.getItem(RELOAD_FLAG) !== "1") {
            window.sessionStorage.setItem(RELOAD_FLAG, "1");
            window.location.reload();
          }
        } catch { window.location.reload(); }
        // Return a no-op component so React doesn't crash before reload.
        return { default: (() => null) as unknown as T };
      }
      throw err;
    }
  });
}

const Home = lazyWithReload(() => import("./pages/Home"));
const Payloads = lazyWithReload(() => import("./pages/Payloads"));
const Consumed = lazyWithReload(() => import("./pages/Consumed"));
const Exceptions = lazyWithReload(() => import("./pages/Exceptions"));
const Servers = lazyWithReload(() => import("./pages/Servers"));
const Users = lazyWithReload(() => import("./pages/Users"));
const Scan = lazyWithReload(() => import("./pages/Scan"));
const ScanPids = lazyWithReload(() => import("./pages/ScanPids"));
const ScanDispatch = lazyWithReload(() => import("./pages/ScanDispatch"));
const StorageRelay = lazyWithReload(() => import("./pages/StorageRelay"));
const Queues = lazyWithReload(() => import("./pages/Queues"));
const QueuesFailed = lazyWithReload(() => import("./pages/QueuesFailed"));
const Trace = lazyWithReload(() => import("./pages/Trace"));
const RecentActivity = lazyWithReload(() => import("./pages/RecentActivity"));
const Settings = lazyWithReload(() => import("./pages/Settings"));
const OtpLogin = lazyWithReload(() => import("./pages/OtpLogin"));

function PageFallback() {
  return (
    <Center mih="60vh">
      <Loader color="brand" />
    </Center>
  );
}

function FullPageFallback() {
  return (
    <Center mih="100vh">
      <Loader color="brand" />
    </Center>
  );
}

export default function App() {
  const { status } = useAuth();

  if (status === "loading") {
    return <FullPageFallback />;
  }

  if (status === "anonymous") {
    return (
      <Suspense fallback={<FullPageFallback />}>
        <OtpLogin />
      </Suspense>
    );
  }

  return (
    <AppShell>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/payloads" element={<Payloads />} />
          <Route path="/consumed" element={<Consumed />} />
          <Route path="/exceptions" element={<Exceptions />} />
          <Route path="/scan" element={<Scan />} />
          <Route path="/scan-pids" element={<ScanPids />} />
          <Route path="/scan-dispatch" element={<ScanDispatch />} />
          <Route path="/storage-relay" element={<StorageRelay />} />
          <Route path="/queues" element={<Queues />} />
          <Route path="/queues-failed" element={<QueuesFailed />} />
          <Route path="/servers" element={<Servers />} />
          <Route path="/users" element={<Users />} />
          <Route path="/recent-activity" element={<RecentActivity />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/trace/:pid" element={<Trace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}
